// web/src/player/highlightGate.ts
// Gates the caption highlight on whether audio is actually playing right now.
// STT timings have no silence gaps (Cloud STT absorbs pauses into adjacent word
// durations), so a word's `start` can precede its audible onset by up to ~1s.
// This gate keeps the highlight visible only while sound is being produced, with
// a short release so brief inter-word/stop-consonant dips don't flicker the pill.

/** Seconds to keep the highlight active after speech stops (anti-flicker release). */
export const HIGHLIGHT_RELEASE = 0.18;

export class HighlightGate {
  private lastSpeech = -Infinity;
  private heldIndex = -1;
  private ended = false;

  constructor(private readonly release: number = HIGHLIGHT_RELEASE) {}

  /**
   * Returns whether the caption highlight should be shown at `time` (seconds).
   * `currentIndex` is the engine's current word index.
   *
   * Model: a word is highlighted from when it starts sounding until its utterance
   * ends (a silence longer than the release bridge). Two consequences:
   *  - The next word never lights before it is spoken: when the engine advances to
   *    a new word during silence (which it does immediately, since STT timings are
   *    gapless), there is no audio yet, so it stays dark until actually spoken.
   *  - A word is not re-lit once its utterance has ended. Cloud STT gives short
   *    words ("a", "the") over-long windows; audio resuming inside that window is
   *    the NEXT word bleeding in, not the word again — so it must not flicker back.
   * The release window still bridges brief mid-word amplitude dips so syllable gaps
   * and stop consonants don't blink the highlight.
   */
  update(time: number, speaking: boolean, currentIndex: number): boolean {
    if (currentIndex !== this.heldIndex) {
      this.heldIndex = currentIndex;
      this.lastSpeech = -Infinity;
      this.ended = false;
    }
    if (speaking) {
      if (this.ended) return false;       // resumed audio belongs to the next word bleeding in
      this.lastSpeech = time;
      return true;
    }
    const dt = time - this.lastSpeech;
    if (dt >= 0 && dt < this.release) return true;   // brief same-word dip
    if (this.lastSpeech > -Infinity) this.ended = true; // word had been spoken -> its utterance is over
    return false;
  }

  reset(): void {
    this.lastSpeech = -Infinity;
    this.heldIndex = -1;
    this.ended = false;
  }
}
